import React, { useState } from 'react';
import { Check, Loader2, Shield, Info, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { supabase } from '../supabaseClient';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from './ui/dialog';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from './ui/tooltip';
import { useSubscription } from '../contexts/SubscriptionContext';

const plans = [
    {
        name: 'Free',
        price: 0,
        description: 'Perfect for trying out',
        features: [
            '1 project',
            'Up to 3 screens per flow',
            { text: '10,000 users/month', help: '1 user = 1 unique session' },
            '3 AI Generations/mo',
            '10 AI Edits/mo',
            'Community support'
        ],
        cta: 'Get started',
        popular: false,
        priceId: null
    },
    {
        name: 'Starter',
        price: 29,
        description: 'For growing startups',
        features: [
            'Everything in Free, plus:',
            '3 projects',
            'Unlimited screens per flow',
            { text: '100,000 users/month', help: '1 user = 1 unique session' },
            '25 AI Generations/mo',
            '100 AI Edits/mo',
            'A/B Testing (2 variants)',
            'Full analytics dashboard',
            'Email support'
        ],
        cta: 'Get started',
        popular: true,
        priceId: process.env.REACT_APP_STRIPE_PRICE_STARTER
    },
    {
        name: 'Growth',
        price: 99,
        description: 'For established companies',
        features: [
            'Everything in Starter, plus:',
            'Unlimited projects',
            'Unlimited screens',
            { text: '250,000 users/month', help: '1 user = 1 unique session' },
            '100 AI Generations/mo',
            '500 AI Edits/mo',
            'Unlimited A/B test variants',
            'Priority support'
        ],
        cta: 'Get started',
        popular: false,
        priceId: process.env.REACT_APP_STRIPE_PRICE_GROWTH
    }
];

export default function PricingPage({ onClose, limitReason }) {
    const { subscription } = useSubscription();
    const currentPlan = subscription?.plan_type || 'free';
    const [loading, setLoading] = useState(false);
    const [selectedPlan, setSelectedPlan] = useState(null);

    const handleSelectPlan = async (plan) => {
        if (plan.price === 0) {
            // Free plan - just close
            onClose?.();
            return;
        }

        setLoading(true);
        setSelectedPlan(plan.name);

        try {
            const { data: { user } } = await supabase.auth.getUser();

            if (!user) {
                alert('Please log in to upgrade your plan');
                setLoading(false);
                setSelectedPlan(null);
                return;
            }

            console.log('Initiating checkout for:', plan.name);
            console.log('Price ID:', plan.priceId);

            // Call Supabase Edge Function to create checkout session
            const response = await fetch(
                `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/create-checkout`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`
                    },
                    body: JSON.stringify({
                        priceId: plan.priceId,
                        userId: user.id,
                        email: user.email
                    })
                }
            );

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to create checkout session');
            }

            const { url } = data;

            // Redirect to Stripe Checkout
            window.location.href = url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
            alert(`Checkout failed: ${error.message}`);
            setLoading(false);
            setSelectedPlan(null);
        }
    };

    return (
        <div className="py-12 px-4">
            <div className="max-w-7xl mx-auto">
                {limitReason && (
                    <div className="max-w-3xl mx-auto mb-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-center gap-3 text-yellow-800">
                        <Shield className="h-5 w-5" />
                        <p className="font-medium">
                            {typeof limitReason === 'string'
                                ? limitReason
                                : limitReason.reason === 'projects'
                                    ? `You've reached the ${limitReason.limit} project limit on your current plan.`
                                    : limitReason.reason === 'screens'
                                        ? `You've used ${limitReason.current} screens (Limit: ${limitReason.limit}). Upgrade to publish!`
                                        : limitReason.reason === 'analytics'
                                            ? 'Upgrade to access Advanced Analytics!'
                                            : limitReason.reason === 'published_flows'
                                                ? "You've reached your published flows limit. Upgrade to publish more!"
                                                : 'Upgrade your plan to unlock more features.'}
                        </p>
                    </div>
                )}

                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold mb-4 flex items-center justify-center gap-3">
                        Simple, Transparent Pricing
                        {process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY?.startsWith('pk_test_') && (
                            <span className="text-xs font-mono bg-yellow-100 text-yellow-800 px-2 py-1 rounded border border-yellow-200 align-middle">
                                TEST MODE
                            </span>
                        )}
                    </h1>
                    <p className="text-xl text-muted-foreground">
                        Choose the plan that's right for you
                    </p>
                    {currentPlan !== 'free' && (
                        <p className="text-sm text-muted-foreground mt-2">
                            Current plan: <span className="font-semibold capitalize">{currentPlan}</span>
                        </p>
                    )}
                </div>

                <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
                    {plans.map((plan) => {
                        const isCurrentPlan = plan.name.toLowerCase() === currentPlan;
                        const isLoading = loading && selectedPlan === plan.name;

                        return (
                            <Card
                                key={plan.name}
                                className={`p-8 relative ${plan.popular ? 'border-primary border-2 shadow-lg scale-105' : ''}`}
                            >
                                {plan.popular && (
                                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                                        <div className="bg-primary text-primary-foreground text-sm font-semibold px-4 py-1 rounded-full">
                                            Most Popular
                                        </div>
                                    </div>
                                )}

                                <div className="text-center mb-6">
                                    <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                                    <p className="text-muted-foreground text-sm mb-4">{plan.description}</p>

                                    <div className="mb-6">
                                        <span className="text-5xl font-bold">${plan.price}</span>
                                        <span className="text-muted-foreground">/month</span>
                                    </div>
                                </div>

                                <Button
                                    className="w-full mb-6"
                                    variant={plan.popular ? 'default' : 'outline'}
                                    onClick={() => handleSelectPlan(plan)}
                                    disabled={loading || isCurrentPlan}
                                >
                                    {isLoading ? (
                                        <>
                                            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                            Loading...
                                        </>
                                    ) : isCurrentPlan ? (
                                        'Current Plan'
                                    ) : (
                                        plan.cta
                                    )}
                                </Button>

                                <TooltipProvider>
                                    <ul className="space-y-3">
                                        {plan.features.map((feature, idx) => {
                                            // Handle object features (like users/month with help text)
                                            if (typeof feature === 'object' && feature.text) {
                                                return (
                                                    <li key={idx} className="flex items-start gap-3">
                                                        <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                        <div className="flex items-center gap-1.5">
                                                            <span className="text-sm">{feature.text}</span>
                                                            {feature.help && (
                                                                <Tooltip>
                                                                    <TooltipTrigger>
                                                                        <Info className="h-4 w-4 text-muted-foreground/70 hover:text-foreground transition-colors" />
                                                                    </TooltipTrigger>
                                                                    <TooltipContent>
                                                                        <p>{feature.help}</p>
                                                                    </TooltipContent>
                                                                </Tooltip>
                                                            )}
                                                        </div>
                                                    </li>
                                                );
                                            }

                                            // Handle string features (check for AI keywords)
                                            const isAIGen = typeof feature === 'string' && feature.includes('AI Generations');
                                            const isAIEdit = typeof feature === 'string' && feature.includes('AI Edits');

                                            return (
                                                <li key={idx} className="flex items-start gap-3">
                                                    <Check className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                                                    <div className="flex items-center gap-1.5">
                                                        <span className="text-sm">{feature}</span>
                                                        {(isAIGen || isAIEdit) && (
                                                            <Tooltip>
                                                                <TooltipTrigger>
                                                                    <HelpCircle className="h-3.5 w-3.5 text-muted-foreground/70" />
                                                                </TooltipTrigger>
                                                                <TooltipContent>
                                                                    <p className="max-w-[200px] text-xs">
                                                                        {isAIGen
                                                                            ? "Create full multi-screen onboarding flows from a single prompt."
                                                                            : "Ask AI to modify specific screens or elements using natural language."}
                                                                    </p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </li>
                                            );
                                        })}
                                    </ul>
                                </TooltipProvider>
                            </Card>
                        );
                    })}
                </div>

                <div className="text-center mt-12 text-sm text-muted-foreground">
                    <p>Cancel anytime. No long-term contracts.</p>
                    <p className="mt-2">Need a custom plan? <a href="mailto:support@poponboarding.com" className="text-primary hover:underline">Contact us</a></p>
                </div>
            </div>
        </div>
    );
}
